import React, { useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, { Field } from '@atlaskit/form';
import { RovoIcon } from '@atlaskit/logo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { useAIGeneratedCoverImage } from '../../hooks/useAIGeneratedCoverImage';
import { IMAGE_GENERATION_THEMES, type ImageGenerationTheme } from '../../types';

export type AIGeneratedMediaState = {
	collection: string | undefined;
	dimensions?: { height: number; width: number };
	fileMimeType?: string;
	id: string | undefined;
};

export type ImageFromAIGenerationProps = {
	/**
	 * Callback to close the popup/container after insertion.
	 */
	closePopup: () => void;
	/**
	 * Callback to insert the generated media into the editor.
	 */
	insertMedia: (mediaState: AIGeneratedMediaState) => void;
	/**
	 * The media upload collection identifier.
	 * Must have an uploadParams.collection set.
	 */
	mediaUploadCollection: string | undefined;
};

const i18n = defineMessages({
	getCreativeLabel: {
		id: 'ai-image-generation.image-from-ai-generation.get-creative-label',
		defaultMessage: 'Get creative with Rovo',
		description: 'Label text for the Rovo image generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.image-from-ai-generation.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description: 'Alternative text for the Rovo icon in the AI image generation interface.',
	},
	describeImagePrompt: {
		id: 'ai-image-generation.image-from-ai-generation.describe-image-prompt',
		defaultMessage: 'Describe an image to create',
		description: 'Prompt text for AI image generation',
	},
	generateImage: {
		id: 'ai-image-generation.image-from-ai-generation.generate-image',
		defaultMessage: 'Generate image',
		description: 'Button text for generating an image through AI',
	},
	pickStyleLabel: {
		id: 'ai-image-generation.image-from-ai-generation.pick-style-label',
		defaultMessage: 'Pick a style',
		description: 'Label for the style/theme picker dropdown',
	},
	stylePhotorealistic: {
		id: 'ai-image-generation.image-from-ai-generation.style-photorealistic',
		defaultMessage: 'Photorealistic',
		description:
			'Style option in the image generation dropdown that creates realistic photograph-like images.',
	},
	styleAbstract: {
		id: 'ai-image-generation.image-from-ai-generation.style-abstract',
		defaultMessage: 'Abstract',
		description:
			'Style option in the image generation dropdown that creates artistic images with geometric shapes and creative interpretations.',
	},
	styleWatercolors: {
		id: 'ai-image-generation.image-from-ai-generation.style-watercolors',
		defaultMessage: 'Watercolors',
		description:
			'Style option in the image generation dropdown that creates watercolor painting-style images with soft, flowing colors.',
	},
	styleGraphicDesign: {
		id: 'ai-image-generation.image-from-ai-generation.style-graphic-design',
		defaultMessage: 'Graphic Design',
		description:
			'Style option in the image generation dropdown that creates modern design images with clean layouts and bold typography.',
	},
	styleLandscape: {
		id: 'ai-image-generation.image-from-ai-generation.style-landscape',
		defaultMessage: 'Landscape',
		description:
			'Style option in the image generation dropdown that creates landscape images featuring natural or urban scenery.',
	},
});

const styles = cssMap({
	buttonWrapper: {
		marginTop: token('space.150'),
	},
	rovoIcon: {
		width: '16px',
		height: '16px',
		marginRight: token('space.050'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	headerWrapper: {
		marginBottom: token('space.100'),
	},
});

export function ImageFromAIGeneration({
	mediaUploadCollection,
	insertMedia,
	closePopup,
}: ImageFromAIGenerationProps): React.JSX.Element {
	const intl = useIntl();

	const [selectedTheme, setSelectedTheme] = useState<{
		label: string;
		value: ImageGenerationTheme;
	} | null>(null);

	const themeOptions = [
		{
			label: intl.formatMessage(i18n.stylePhotorealistic),
			value: IMAGE_GENERATION_THEMES.photorealistic,
		},
		{ label: intl.formatMessage(i18n.styleAbstract), value: IMAGE_GENERATION_THEMES.abstract },
		{
			label: intl.formatMessage(i18n.styleWatercolors),
			value: IMAGE_GENERATION_THEMES.watercolors,
		},
		{
			label: intl.formatMessage(i18n.styleGraphicDesign),
			value: IMAGE_GENERATION_THEMES.graphic_design,
		},
		{
			label: intl.formatMessage(i18n.styleLandscape),
			value: IMAGE_GENERATION_THEMES.landscape,
		},
	];

	const contentId = mediaUploadCollection?.match(/\d+$/)?.[0] ?? '';
	const { generateImage, aiHeaderImageStatus, aiHeaderImageFileId } = useAIGeneratedCoverImage({
		contentId,
	});

	const isLoading = aiHeaderImageStatus === 'LOADING';

	const handleSubmit = (prompt: string) => {
		const useRawPrompt = !selectedTheme?.value;

		generateImage({
			adfContent: prompt,
			useRawPrompt,
			...(selectedTheme?.value && { theme: selectedTheme.value }),
		});
	};

	// Insert generated image into editor once available
	useEffect(() => {
		if (aiHeaderImageStatus === 'NOT_LOADING' && aiHeaderImageFileId) {
			insertMedia({
				id: aiHeaderImageFileId,
				collection: mediaUploadCollection,
				fileMimeType: 'image/png',
				dimensions: {
					width: 1260,
					height: 560,
				},
			});
			closePopup();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aiHeaderImageStatus, aiHeaderImageFileId]);

	return (
		<Stack grow="fill">
			<Box xcss={styles.headerWrapper}>
				<Inline space="space.075" alignBlock="center">
					<Box xcss={styles.rovoIcon}>
						<RovoIcon
							appearance="brand"
							size="xsmall"
							label={intl.formatMessage(i18n.rovoIconAlt)}
						/>
					</Box>
					<Text size="medium">{intl.formatMessage(i18n.getCreativeLabel)}</Text>
				</Inline>
			</Box>

			<Form
				onSubmit={(formData: { 'image-generation-prompt': string }) =>
					handleSubmit(formData['image-generation-prompt'])
				}
			>
				{({ formProps }) => (
					<form {...formProps}>
						<Field
							label={intl.formatMessage(i18n.pickStyleLabel)}
							name="image-generation-theme"
							isDisabled={isLoading}
						>
							{() => (
								<Select
									inputId="image-generation-theme-picker"
									aria-label={intl.formatMessage(i18n.pickStyleLabel)}
									options={themeOptions}
									value={selectedTheme}
									onChange={(newValue) => setSelectedTheme(newValue)}
									placeholder={intl.formatMessage(i18n.pickStyleLabel)}
									isDisabled={isLoading}
								/>
							)}
						</Field>
						<Field
							label={intl.formatMessage(i18n.describeImagePrompt)}
							name="image-generation-prompt"
							isDisabled={isLoading}
						>
							{({ fieldProps }: any) => <TextArea placeholder="" {...fieldProps} />}
						</Field>
						<Box xcss={styles.buttonWrapper}>
							<Button
								appearance="primary"
								isLoading={isLoading}
								isDisabled={isLoading}
								type="submit"
								shouldFitContainer
							>
								{intl.formatMessage(i18n.generateImage)}
							</Button>
						</Box>
					</form>
				)}
			</Form>
		</Stack>
	);
}
